// ─────────────────────────────────────────────────────────────────────────────
// KLOTHIQ — EditSaleMetadataModal
// ─────────────────────────────────────────────────────────────────────────────
// Isolated modal for safely updating customer contact info and order notes
// on any sale order (including delivered/paid) without touching inventory or payments.
// ─────────────────────────────────────────────────────────────────────────────

import UserGuide from '@/components/help/UserGuide';
import { Input, Textarea } from '@/components/ui/FormControls';
import { useState, useEffect, useCallback } from 'react';
import { X, User, Phone, MapPin, FileText, Loader2, Check } from 'lucide-react';
import { useCustomers } from '@/hooks/useCustomers';
import { useDebounce } from '@/hooks/useDebounce';
import { useUpdateSaleMetadata } from '@/hooks/useSales';
import ComboSelect from '@/components/common/ComboSelect';
import { toast } from 'sonner';

export interface EditSaleMetadataModalProps {
  isOpen: boolean;
  onClose: () => void;
  saleId: string;
  saleNumber: string;
  expectedUpdatedAt?: string;
  initialCustomerId?: string | null;
  initialCustomerName?: string | null;
  initialCustomerMobile?: string | null;
  initialCustomerAddress?: string | null;
  initialNotes?: string | null;
  onSuccess?: () => void;
}

export default function EditSaleMetadataModal({
  isOpen,
  onClose,
  saleId,
  saleNumber,
  expectedUpdatedAt,
  initialCustomerId = null,
  initialCustomerName = '',
  initialCustomerMobile = '',
  initialCustomerAddress = '',
  initialNotes = '',
  onSuccess,
}: EditSaleMetadataModalProps) {
  const updateMetadata = useUpdateSaleMetadata();

  // ── Form State ──
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(initialCustomerId ?? null);
  const [customerName, setCustomerName] = useState(initialCustomerName || '');
  const [customerMobile, setCustomerMobile] = useState(initialCustomerMobile || '');
  const [customerAddress, setCustomerAddress] = useState(initialCustomerAddress || '');
  const [notes, setNotes] = useState(initialNotes || '');
  const [error, setError] = useState<string | null>(null);

  // ── Customer Search State ──
  const [customerSearch, setCustomerSearch] = useState('');
  const debouncedSearch = useDebounce(customerSearch, 300);
  const { data: customers = [], isFetching: customersLoading } = useCustomers(
    debouncedSearch.length >= 2 ? debouncedSearch : undefined
  );

  // Sync state whenever modal opens or props change
  useEffect(() => {
    if (isOpen) {
      setSelectedCustomerId(initialCustomerId ?? null);
      setCustomerName(initialCustomerName || '');
      setCustomerMobile(initialCustomerMobile || '');
      setCustomerAddress(initialCustomerAddress || '');
      setNotes(initialNotes || '');
      setError(null);
      setCustomerSearch('');
    }
  }, [
    isOpen,
    initialCustomerId,
    initialCustomerName,
    initialCustomerMobile,
    initialCustomerAddress,
    initialNotes,
  ]);

  // Handle escape key to dismiss
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !updateMetadata.isPending) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, updateMetadata.isPending]);

  // When a registered customer is selected, auto-fill contact fields
  const handleCustomerSelect = useCallback(
    (val: string | undefined) => {
      if (!val) {
        setSelectedCustomerId(null);
        return;
      }
      setSelectedCustomerId(val);
      const found = customers.find((c) => c.id === val);
      if (found) {
        setCustomerName(found.name);
        if (found.phone) setCustomerMobile(found.phone);
        const fullAddr = [found.address, found.city, found.pincode].filter(Boolean).join(', ');
        if (fullAddr) setCustomerAddress(fullAddr);
      }
    },
    [customers]
  );

  const customerOptions = customers.map((c) => ({
    value: c.id,
    label: c.name,
    sublabel: c.phone ?? undefined,
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = customerName.trim();
    if (!trimmedName) {
      setError('Customer name is required');
      return;
    }
    setError(null);

    try {
      await updateMetadata.mutateAsync({
        sale_id: saleId,
        expected_updated_at: expectedUpdatedAt,
        customer_id: selectedCustomerId || null,
        customer_name: trimmedName,
        customer_mobile: customerMobile.trim() || null,
        customer_address: customerAddress.trim() || null,
        notes: notes.trim() || null,
      });

      toast.success('Customer details updated successfully.');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update customer details';

      if (errorMessage.includes('modified by another user')) {
        toast.warning(errorMessage, {
          action: { label: 'Reload', onClick: () => window.location.reload() },
        });
      } else {
        toast.error(errorMessage);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-in fade-in duration-150"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-metadata-title" data-field-guide-scope
    >
      <div className="w-full max-w-lg rounded-xl border border-border bg-card p-5 sm:p-6 shadow-2xl space-y-4 text-foreground">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <User className="h-4 w-4" />
            </div>
            <div>
              <div className="flex items-start justify-between gap-3 pr-8">
                <h3 id="edit-metadata-title" className="text-base font-bold text-foreground">
                  Edit Customer Details
                </h3>
                <UserGuide compact />
              </div>
              <p className="text-xs text-muted-foreground font-mono">
                Order {saleNumber}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={updateMetadata.isPending}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors disabled:opacity-50"
            aria-label="Close modal"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3.5">
          {/* Link to existing customer */}
          <div className="space-y-1">
            <label
              htmlFor="customer-select"
              className="text-xs font-semibold text-muted-foreground"
            >
              Link Registered Customer (Optional)
            </label>
            <ComboSelect
              id="customer-select"
              value={selectedCustomerId ?? ''}
              onChange={handleCustomerSelect}
              options={customerOptions}
              onSearch={setCustomerSearch}
              isLoading={customersLoading}
              placeholder="Search by customer name or phone..."
              searchPlaceholder="Type customer name or phone..."
              clearable
              disabled={updateMetadata.isPending}
              helpLabel="Link Registered Customer (Optional)"
            />
            <p className="text-[11px] text-muted-foreground">
              Selecting a customer auto-populates the name, phone, and address below.
            </p>
          </div>

          {/* Customer Name */}
          <div className="space-y-1">
            <label
              htmlFor="customer-name-input"
              className="text-xs font-semibold text-foreground"
            >
              Customer Name <span className="text-destructive">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                id="customer-name-input"
                type="text"
                required
                disabled={updateMetadata.isPending}
                value={customerName}
                onChange={(e) => {
                  setCustomerName(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="e.g. Manikandan"
                className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                helpLabel="Customer Name *"
              />
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>

          {/* Phone Number */}
          <div className="space-y-1">
            <label
              htmlFor="customer-phone-input"
              className="text-xs font-semibold text-foreground"
            >
              Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                id="customer-phone-input"
                type="tel"
                disabled={updateMetadata.isPending}
                value={customerMobile}
                onChange={(e) => setCustomerMobile(e.target.value)}
                placeholder="+91 98765 43210"
                className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                helpLabel="Phone Number"
              />
            </div>
          </div>

          {/* Address */}
          <div className="space-y-1">
            <label
              htmlFor="customer-address-input"
              className="text-xs font-semibold text-foreground"
            >
              Delivery Address
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Textarea
                id="customer-address-input"
                rows={2}
                disabled={updateMetadata.isPending}
                value={customerAddress}
                onChange={(e) => setCustomerAddress(e.target.value)}
                placeholder="Door no, street, city, pincode..."
                className="flex w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 resize-none"
                helpLabel="Delivery Address"
              />
            </div>
          </div>

          {/* Order Notes */}
          <div className="space-y-1">
            <label
              htmlFor="order-notes-input"
              className="text-xs font-semibold text-foreground"
            >
              Order Notes / Delivery Remarks
            </label>
            <div className="relative">
              <FileText className="absolute left-3 top-3 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Textarea
                id="order-notes-input"
                rows={2}
                disabled={updateMetadata.isPending}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Special delivery instructions, gift packing, etc..."
                className="flex w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 resize-none"
                helpLabel="Order Notes / Delivery Remarks"
              />
            </div>
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              disabled={updateMetadata.isPending}
              className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent text-foreground transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMetadata.isPending || !customerName.trim()}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateMetadata.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
